import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { AboutSection } from './components/AboutSection';
import { WhyChooseUsSection } from './components/WhyChooseUsSection';
import { ServicesSection } from './components/ServicesSection';
import { IndustryWorksSection } from './components/IndustryWorksSection';
import { ProductsSection } from './components/ProductsSection';
import { TestimonialsSection } from './components/TestimonialsSection';
import { CertificationsSection } from './components/CertificationsSection';
import { FaqSection } from './components/FaqSection';
import { ClientLogosMarquee } from './components/ClientLogosMarquee';
import { Footer } from './components/Footer';
import { ConsultationCtaSection } from './components/ConsultationCtaSection';
import { SideQuickDock } from './components/SideQuickDock';
import { BackToTop } from './components/BackToTop';
import { WebsiteLoader } from './components/WebsiteLoader';
import { SeoHead } from './components/SeoHead';
import { CmsProvider, useCms } from './context/CmsContext';
import { TechnicalDatasheet, ServiceItem, IndustryVertical } from './types';
import type { TrustTabId } from './components/TrustComplianceModal';

// Code-split ALL routes & portals — only the home page sections are in the initial bundle
const AdminLogin = lazy(() => import('./components/admin/AdminLogin').then((m) => ({ default: m.AdminLogin })));
const AdminPortal = lazy(() => import('./components/admin/AdminPortal').then((m) => ({ default: m.AdminPortal })));
const AboutPage = lazy(() => import('./components/AboutPage').then((m) => ({ default: m.AboutPage })));
const ServicesPage = lazy(() => import('./components/ServicesPage').then((m) => ({ default: m.ServicesPage })));
const IndustryPage = lazy(() => import('./components/IndustryPage').then((m) => ({ default: m.IndustryPage })));
const ProductPage = lazy(() => import('./components/ProductPage').then((m) => ({ default: m.ProductPage })));
const ClientsPage = lazy(() => import('./components/ClientsPage').then((m) => ({ default: m.ClientsPage })));
const ContactPage = lazy(() => import('./components/ContactPage').then((m) => ({ default: m.ContactPage })));
const SiemensSolutionPartnerPage = lazy(() => import('./components/SiemensSolutionPartnerPage').then((m) => ({ default: m.SiemensSolutionPartnerPage })));

// Code-split modals and drawers: only downloaded on demand
const EngineeringRfqModal = lazy(() => import('./components/EngineeringRfqModal').then((m) => ({ default: m.EngineeringRfqModal })));
const TrustComplianceModal = lazy(() => import('./components/TrustComplianceModal').then((m) => ({ default: m.TrustComplianceModal })));
const DatasheetModal = lazy(() => import('./components/DatasheetModal').then((m) => ({ default: m.DatasheetModal })));
const ServiceModal = lazy(() => import('./components/ServiceModal').then((m) => ({ default: m.ServiceModal })));
const IndustryModal = lazy(() => import('./components/IndustryModal').then((m) => ({ default: m.IndustryModal })));
const ConnectDrawer = lazy(() => import('./components/ConnectDrawer').then((m) => ({ default: m.ConnectDrawer })));

export type AppPageId = 'home' | 'about' | 'services' | 'industry' | 'product' | 'clients' | 'contact' | 'admin' | 'siemens-solution-partner';

function AppContent({ initialPage = 'home' }: { initialPage?: AppPageId }) {
  // Resolve page from either clean URL pathname (/services, /about, /admin, etc.) or legacy hash (#services)
  const resolvePageFromLocation = useCallback((): AppPageId => {
    if (typeof window === 'undefined') return initialPage;

    // 1. Check clean pathname
    const pathname = window.location.pathname.toLowerCase().replace(/^\/|\/$/g, '');
    const cleanPath = pathname.replace(/\.html$/, '');

    if (
      cleanPath === 'admin' ||
      cleanPath.startsWith('admin/') ||
      cleanPath === 'admin-panel' ||
      cleanPath.startsWith('admin-panel/')
    ) return 'admin';
    if (
      cleanPath === 'about' ||
      cleanPath === 'about-us' ||
      cleanPath === 'profile' ||
      cleanPath === 'company-insights' ||
      cleanPath === 'corporate-presentation' ||
      cleanPath === 'corporate-video' ||
      cleanPath === 'mission-vision' ||
      cleanPath === 'our-advantage' ||
      cleanPath === 'quality'
    ) return 'about';

    if (
      cleanPath === 'services' ||
      cleanPath === 'services-page' ||
      cleanPath === 'service' ||
      cleanPath === 'control-panels' ||
      cleanPath === 'electric-control-panel' ||
      cleanPath === 'plc-control-panel' ||
      cleanPath === 'project-methodology' ||
      cleanPath === 'validation-services'
    ) return 'services';

    if (cleanPath === 'industry' || cleanPath === 'industries' || cleanPath === 'service-details' || cleanPath.startsWith('industry/')) return 'industry';

    if (
      cleanPath === 'product' ||
      cleanPath === 'products' ||
      cleanPath === 'catalogue' ||
      cleanPath === 'catalog' ||
      cleanPath === 'product-catalogue' ||
      cleanPath === 'e-catalogue' ||
      cleanPath === 'ecatalogue' ||
      cleanPath === 'products-list' ||
      cleanPath.startsWith('product/') ||
      cleanPath.startsWith('products/') ||
      cleanPath.startsWith('pro-') ||
      cleanPath.startsWith('catalog/') ||
      cleanPath.startsWith('catalogue/') ||
      [
        'siemens-vfd',
        'sinamics-g120-vfd',
        'sinamics-v20-vfd',
        'dc-and-ac-drive',
        'siemens-pm240-2-ac-drives',
        'siemens-micromaster-drives',
        'siemens-servo-drives',
        'siemens-v90-servo-system',
        'siemens-6ra80-dc-drives',
        'siemens-plc',
        'siemens-plc-products',
        'siemens-plc-system',
        'siemens-s7-1200-plc',
        'siemens-s71500',
        'simatics-s7-200-smart-plc',
        'siemens-sitop-module',
        'siemens-hmi',
        'siemens-scada-system',
        'simatic-wincc-scada',
        'siemens-wincc-advanced',
        'industrial-panel-pc',
        'et-200-sp-remote-io-station',
        'servo-motor',
        'siemens-1ph8-motors'
      ].includes(cleanPath)
    ) return 'product';

    if (
      cleanPath === 'clients' ||
      cleanPath === 'client' ||
      cleanPath === 'testimonial' ||
      cleanPath === 'testimonials' ||
      cleanPath === 'photos'
    ) return 'clients';

    if (
      cleanPath === 'contact' ||
      cleanPath === 'contact-us' ||
      cleanPath === 'enquiry'
    ) return 'contact';

    if (
      cleanPath === 'siemens-solution-partner' ||
      cleanPath === 'siemens-partner' ||
      cleanPath === 'siemens'
    ) return 'siemens-solution-partner';

    // 2. Fallback to hash if present
    const hash = window.location.hash.replace('#', '').toLowerCase();
    if (
      hash === 'admin' ||
      hash.startsWith('admin/') ||
      hash.startsWith('/admin/') ||
      hash === '/admin' ||
      hash === 'admin-panel' ||
      hash.startsWith('admin-panel/')
    ) return 'admin';
    // When on home page without cleanPath, section anchors (#products, #industry, etc.) stay on home page
    if (!cleanPath) {
      if (hash.startsWith('/')) {
        const routeHash = hash.replace(/^\//, '');
        if (routeHash === 'product' || routeHash === 'products' || routeHash === 'catalogue') return 'product';
        if (routeHash === 'industry') return 'industry';
        if (routeHash === 'services') return 'services';
        if (routeHash === 'about') return 'about';
        if (routeHash === 'clients') return 'clients';
        if (routeHash === 'contact') return 'contact';
      }
      return 'home';
    }

    if (hash === 'about') return 'about';
    if (hash === 'services' || hash === 'services-page') return 'services';
    if (hash === 'industry' || hash === 'industries') return 'industry';
    if (hash === 'product' || hash === 'products' || hash === 'catalogue') return 'product';
    if (hash === 'clients' || hash === 'client') return 'clients';
    if (hash === 'contact') return 'contact';
    if (
      hash === 'siemens-solution-partner' ||
      hash === 'siemens-partner' ||
      hash === 'siemens'
    ) return 'siemens-solution-partner';

    return 'home';
  }, [initialPage]);

  const getProductCategoryFromLocation = useCallback((): string => {
    if (typeof window === 'undefined') return '';
    const pathname = window.location.pathname.toLowerCase().replace(/^\/|\/$/g, '');
    const cleanPath = pathname.replace(/\.html$/, '');
    if (cleanPath.startsWith('product/')) {
      return cleanPath.replace(/^product\//, '').replace(/\/$/, '');
    }
    if (cleanPath.startsWith('products/')) {
      return cleanPath.replace(/^products\//, '').replace(/\/$/, '');
    }
    // Legacy product URLs mapped directly to specific product category slugs
    if (['siemens-vfd', 'sinamics-g120-vfd', 'sinamics-v20-vfd', 'dc-and-ac-drive', 'siemens-pm240-2-ac-drives', 'siemens-micromaster-drives', 'siemens-servo-drives', 'siemens-v90-servo-system'].includes(cleanPath)) {
      return 'electrical-drives';
    }
    if (['siemens-6ra80-dc-drives'].includes(cleanPath)) {
      return 'dc-drives';
    }
    if (['siemens-plc', 'siemens-plc-products', 'siemens-plc-system', 'siemens-s7-1200-plc', 'siemens-s71500', 'simatics-s7-200-smart-plc', 'siemens-sitop-module'].includes(cleanPath)) {
      return 'simatic-plc';
    }
    if (['siemens-hmi'].includes(cleanPath)) {
      return 'hmi';
    }
    if (['siemens-scada-system', 'simatic-wincc-scada', 'siemens-wincc-advanced', 'industrial-panel-pc'].includes(cleanPath)) {
      return 'scada';
    }
    if (['et-200-sp-remote-io-station'].includes(cleanPath)) {
      return 'distributed-io';
    }
    if (['servo-motor', 'siemens-1ph8-motors'].includes(cleanPath)) {
      return 'motors';
    }
    const params = new URLSearchParams(window.location.search);
    return params.get('category') || params.get('item') || params.get('id') || '';
  }, []);

  const getIndustrySectorFromLocation = useCallback((): string => {
    if (typeof window === 'undefined') return '';
    const pathname = window.location.pathname.toLowerCase().replace(/^\/|\/$/g, '');
    const cleanPath = pathname.replace(/\.html$/, '');
    // Prefer clean path-based URLs: /industry/cable/
    if (cleanPath.startsWith('industry/')) {
      return cleanPath.replace(/^industry\//, '').replace(/\/$/, '');
    }
    // Legacy: hash-based /industry#cable (still supported for backwards links)
    return window.location.hash ? window.location.hash.replace(/^#/, '') : '';
  }, []);

  const [currentPage, setCurrentPage] = useState<AppPageId>(() => resolvePageFromLocation());
  const [productCategoryTarget, setProductCategoryTarget] = useState<string>(() => getProductCategoryFromLocation());
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return (
      sessionStorage.getItem('simatech_admin_auth') === 'true' ||
      localStorage.getItem('simatech_admin_auth') === 'true'
    );
  });
  const [selectedDatasheet, setSelectedDatasheet] = useState<TechnicalDatasheet | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<IndustryVertical | null>(null);
  const [industrySectorTarget, setIndustrySectorTarget] = useState<string>('');
  const [isConnectDrawerOpen, setIsConnectDrawerOpen] = useState<boolean>(false);
  const [inquirySubject, setInquirySubject] = useState<string>('Turnkey Process Automation');
  const [isEngineeringRfqOpen, setIsEngineeringRfqOpen] = useState<boolean>(false);
  const [rfqInitialScope, setRfqInitialScope] = useState<string>('Turnkey PLC & SCADA Process Automation');
  const [isTrustComplianceModalOpen, setIsTrustComplianceModalOpen] = useState<boolean>(false);
  const [trustModalTab, setTrustModalTab] = useState<TrustTabId>('qicc');

  const handleOpenRfq = (scope?: string) => {
    if (scope) setRfqInitialScope(scope);
    setIsEngineeringRfqOpen(true);
  };

  const handleInspectTrust = (tab: TrustTabId) => {
    setTrustModalTab(tab);
    setIsTrustComplianceModalOpen(true);
  };
  const { siteData, loading } = useCms();

  // Synchronize route with browser URL history (Pathname + Popstate + Hashchange)
  useEffect(() => {
    const handleLocationChange = () => {
      const resolved = resolvePageFromLocation();
      setCurrentPage(resolved);
      if (resolved === 'industry') {
        setIndustrySectorTarget(getIndustrySectorFromLocation());
      } else {
        setIndustrySectorTarget('');
      }
      if (resolved === 'product') {
        setProductCategoryTarget(getProductCategoryFromLocation());
      } else {
        setProductCategoryTarget('');
      }

      // Smoothly normalize legacy .html URLs in the browser address bar
      if (typeof window !== 'undefined' && window.location.pathname.endsWith('.html')) {
        const canonicalPath = window.location.pathname.replace(/\.html$/, '') || '/';
        window.history.replaceState(null, '', canonicalPath + window.location.search + window.location.hash);
      }
    };

    handleLocationChange();

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, [resolvePageFromLocation]);

  // Universal Soft Loading Animation Observer for All Sections Across Every Page
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const mainEl = document.querySelector('main');
    if (!mainEl) return;

    mainEl.classList.add('js-section-reveal-ready');

    const getSections = () => {
      return Array.from(
        document.querySelectorAll<HTMLElement>(
          'main section:not(#home):not([aria-label="Hero Section"]), main .soft-section-reveal:not(#home), main [data-section]:not(#home), .client-marquee-section'
        )
      );
    };

    let observer: IntersectionObserver | null = null;

    // Delay slightly to ensure component children have finished mounting into DOM
    const timer = setTimeout(() => {
      const sections = getSections();
      if (sections.length === 0) return;

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('section-revealed');
              observer?.unobserve(entry.target);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -40px 0px',
          threshold: 0.04,
        }
      );

      let aboveFoldCount = 0;
      sections.forEach((sec) => {
        if (sec.classList.contains('section-revealed')) return;
        sec.classList.add('soft-section-reveal');

        const rect = sec.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (inView) {
          aboveFoldCount++;
          sec.classList.add(`stagger-${Math.min(aboveFoldCount, 4)}`);
          requestAnimationFrame(() => {
            setTimeout(() => {
              sec.classList.add('section-revealed');
            }, 60 * aboveFoldCount);
          });
        } else {
          observer?.observe(sec);
        }
      });
    }, 40);

    return () => {
      clearTimeout(timer);
      observer?.disconnect();
    };
  }, [currentPage, siteData]);

  const navigateTo = (page: AppPageId, targetId?: string) => {
    setCurrentPage(page);

    if (typeof window !== 'undefined') {
      if (page !== 'industry') {
        setIndustrySectorTarget('');
      }
      if (page !== 'product') {
        setProductCategoryTarget('');
      }

      if (page === 'home' && targetId) {
        const cleanId = targetId.replace('#', '');
        if (window.location.pathname !== '/' && window.location.pathname !== '') {
          window.history.pushState(null, '', `/#${cleanId}`);
        } else {
          window.location.hash = cleanId;
        }

        setTimeout(() => {
          const el = document.getElementById(cleanId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
        return;
      }

      if (page === 'industry') {
        const cleanId = targetId ? targetId.replace(/^#/, '').replace(/^\//, '').replace(/\/$/, '') : '';
        setIndustrySectorTarget(cleanId);
        // Use clean path URLs for SEO: /industry/cable/ instead of /industry#cable
        const targetPath = cleanId ? `/industry/${cleanId}/` : '/industry/';
        const currentFullPath = window.location.pathname + (window.location.search || '');
        if (currentFullPath !== targetPath && currentFullPath !== targetPath.replace(/\/$/, '')) {
          window.history.pushState(null, '', targetPath);
        }
        if (!cleanId) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }

      if (page === 'product') {
        const cleanCategory = targetId ? targetId.replace(/^\//, '').replace(/\/$/, '') : '';
        setProductCategoryTarget(cleanCategory);
        const targetPath = cleanCategory ? `/product/${cleanCategory}/` : '/product/';
        if (window.location.pathname !== targetPath && window.location.pathname !== targetPath.replace(/\/$/, '') || window.location.search) {
          window.history.pushState(null, '', targetPath);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (page === 'admin' && (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/admin-panel'))) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const targetPath = page === 'home' ? '/' : `/${page}/`;
      if (window.location.pathname + window.location.hash !== targetPath && window.location.pathname !== targetPath.replace(/\/$/, '') || window.location.search) {
        window.history.pushState(null, '', targetPath);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleOpenInquiry = (subject?: string) => {
    if (subject) {
      setInquirySubject(subject);
    }
    navigateTo('contact');
  };

  const handleOpenConnect = () => {
    setIsConnectDrawerOpen(true);
  };

  // If currently in Admin Portal view
  if (currentPage === 'admin') {
    if (!isAdminAuthenticated) {
      return (
        <Suspense fallback={<div className="min-h-screen bg-[#06242a]" />}>
          <AdminLogin
            onLoginSuccess={() => setIsAdminAuthenticated(true)}
            onNavigateHome={() => navigateTo('home')}
          />
        </Suspense>
      );
    }

    return (
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#06242a] text-white">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#38c9de] border-t-transparent rounded-full animate-spin" />
              <span className="text-xs font-mono text-slate-300">Loading Simatech CMS Admin...</span>
            </div>
          </div>
        }
      >
        <AdminPortal
          onNavigateHome={() => navigateTo('home')}
          onLogout={() => {
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem('simatech_admin_auth');
              localStorage.removeItem('simatech_admin_auth');
            }
            setIsAdminAuthenticated(false);
          }}
        />
      </Suspense>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col bg-[#ebedf4] text-[#0d3f48]"
      style={{
        fontFamily: siteData?.brand?.fontFamily || "'General Sans', sans-serif",
        fontWeight: (siteData?.brand?.fontWeight as any) || '400',
      }}
    >
      {/* Clean Website Loader */}
      <WebsiteLoader isLoading={loading} />
      {/* Dynamic SEO & Schema Graph Manager */}
      <SeoHead currentPage={currentPage} productCategorySlug={productCategoryTarget} industrySectorSlug={industrySectorTarget} />

      {/* 1. Universal Top Header with dynamic route awareness */}
      <Header
        currentPage={currentPage}
        onNavigate={navigateTo}
        onOpenInquiry={handleOpenInquiry}
        onOpenConnect={() => setIsConnectDrawerOpen(true)}
      />

      {/* 2. Main Page Body (Home vs Dedicated Pages: About, Services, Industry, Products, Clients, Contact) */}
      <main className="flex-grow relative overflow-x-clip">
        <div key={currentPage} className="cinematic-page-root">
          <div className="cinematic-lens-sweep" />
          <Suspense fallback={
            <div className="min-h-[60vh] flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-[#38c9de] border-t-transparent rounded-full animate-spin" />
            </div>
          }>
          {currentPage === 'contact' ? (
            <ContactPage
              initialSubject={inquirySubject}
              onNavigateHome={(target) => navigateTo('home', target)}
              onOpenInquiry={handleOpenInquiry}
            />
          ) : currentPage === 'about' ? (
            <AboutPage
              onNavigateHome={(target) => navigateTo('home', target)}
              onOpenInquiry={handleOpenInquiry}
              onNavigate={navigateTo}
            />
          ) : currentPage === 'services' ? (
            <ServicesPage
              onNavigateHome={(target) => navigateTo('home', target)}
              onSelectService={setSelectedService}
              onOpenInquiry={handleOpenInquiry}
              onNavigate={navigateTo}
            />
          ) : currentPage === 'industry' ? (
            <IndustryPage
              initialSectorId={industrySectorTarget}
              onNavigateHome={(target) => navigateTo('home', target)}
              onSelectIndustry={setSelectedIndustry}
              onOpenInquiry={handleOpenInquiry}
              onNavigate={navigateTo}
            />
          ) : currentPage === 'product' ? (
            <ProductPage
              initialCategorySlug={productCategoryTarget}
              onNavigateHome={(target) => navigateTo('home', target)}
              onOpenInquiry={handleOpenInquiry}
              onNavigate={navigateTo}
            />
          ) : currentPage === 'clients' ? (
            <ClientsPage
              onNavigateHome={(target) => navigateTo('home', target)}
              onOpenInquiry={handleOpenInquiry}
            />
          ) : currentPage === 'siemens-solution-partner' ? (
            <SiemensSolutionPartnerPage
              onNavigateHome={(target) => navigateTo('home', target)}
              onOpenInquiry={handleOpenInquiry}
              onNavigate={navigateTo}
            />
          ) : (
          (() => {
            const homePage = siteData?.pages?.home;
            const ctaEnabled = homePage?.ctaEnabled !== false && homePage?.cta?.enabled !== false;
            const rawCtaPosition = homePage?.ctaPosition || homePage?.cta?.position;
            // Default to 'before-testimonials' to place CTA directly above "Reviews from Our Valued Industrial Clients"
            const ctaPosition = (!rawCtaPosition || rawCtaPosition === 'bottom' || rawCtaPosition === 'before-testimonials' || rawCtaPosition === 'above-testimonials')
              ? 'before-testimonials'
              : rawCtaPosition;

            const ctaElement = ctaEnabled ? (
              <ConsultationCtaSection
                heading={homePage?.ctaHeading || homePage?.cta?.title}
                subtitle={homePage?.ctaSubtitle || homePage?.cta?.subtitle || homePage?.ctaText}
                buttonText={homePage?.ctaButtonText || homePage?.cta?.buttonText}
                buttonTarget={homePage?.ctaButtonTarget || homePage?.cta?.buttonTarget}
                onOpenInquiry={handleOpenInquiry}
                pageName="Industrial Turnkey Automation"
                typography={homePage?.ctaTypography}
              />
            ) : null;

            return (
              <>
                {/* 1. Full-Bleed Hero Section with Integrated Stats HUD */}
                {homePage?.sectionVisibility?.hero !== false && (
                  <Hero
                    showStats={homePage?.sectionVisibility?.stats !== false}
                    onOpenInquiry={handleOpenInquiry}
                    onOpenConnect={handleOpenConnect}
                    onNavigate={navigateTo}
                    onOpenRfq={handleOpenRfq}
                    onInspectTrust={handleInspectTrust}
                  />
                )}
                {ctaPosition === 'after-hero' && ctaElement}

                {/* 2. Company Overview, Sticky Accordion & Capability Pillars */}
                {homePage?.sectionVisibility?.about !== false && (
                  <AboutSection
                    onOpenInquiry={handleOpenInquiry}
                    onOpenConnect={handleOpenConnect}
                    onNavigate={navigateTo}
                    onNavigateAbout={() => navigateTo('about')}
                    onNavigateContact={() => navigateTo('contact')}
                  />
                )}
                {ctaPosition === 'after-about' && ctaElement}

                {/* 2.5 Why Choose Us Section (Supporting Growth on a Global Scale) */}
                {homePage?.sectionVisibility?.whyUs !== false && (
                  <WhyChooseUsSection
                    onOpenInquiry={handleOpenInquiry}
                    onNavigate={navigateTo}
                    onOpenConnect={handleOpenConnect}
                  />
                )}
                {ctaPosition === 'after-whyUs' && ctaElement}

                {/* 3. Dark Services Section (10 Points Solutions) */}
                {homePage?.sectionVisibility?.services !== false && (
                  <ServicesSection
                    onSelectService={setSelectedService}
                    onOpenInquiry={handleOpenInquiry}
                  />
                )}
                {ctaPosition === 'after-services' && ctaElement}

                {/* 4. Industries We Serve (Iconic Representation) */}
                {homePage?.sectionVisibility?.industries !== false && (
                  <IndustryWorksSection
                    onSelectIndustry={setSelectedIndustry}
                    onOpenInquiry={handleOpenInquiry}
                    onNavigate={navigateTo}
                  />
                )}
                {ctaPosition === 'after-industries' && ctaElement}

                {/* 5. Testimonials (Reviews from Our Valued Industrial Clients) */}
                {homePage?.sectionVisibility?.testimonials !== false && (
                  <TestimonialsSection />
                )}
                {ctaPosition === 'after-testimonials' && ctaElement}

                {/* 6. Certifications & Official Accreditation (QICC & ISO) */}
                {homePage?.sectionVisibility?.certifications !== false && (
                  <CertificationsSection />
                )}
                {ctaPosition === 'after-certifications' && ctaElement}

                {/* 7. Products & Hardware Range (Siemens Drives, PLCs, HMI & SCADA Portfolio - Placed Lower) */}
                {homePage?.sectionVisibility?.products !== false && (
                  <ProductsSection
                    onOpenInquiry={handleOpenInquiry}
                    onNavigate={navigateTo}
                    onOpenConnect={handleOpenConnect}
                  />
                )}
                {(ctaPosition === 'after-products' || ctaPosition === 'before-testimonials' || ctaPosition === 'above-testimonials') && ctaElement}

                {/* 8. FAQ Accordion */}
                {homePage?.sectionVisibility?.faq !== false && (
                  <FaqSection onOpenInquiry={handleOpenInquiry} />
                )}

                {(ctaPosition === 'page-bottom' || ctaPosition === 'after-faq') && ctaElement}
              </>
            );
          })()
        )}
          </Suspense>
        </div>
      </main>

      {/* Partners / Customer Logo Moving Animation Strip on Top of Footer (Hidden on Clients page) */}
      {currentPage !== 'clients' && (currentPage !== 'home' || siteData?.pages?.home?.sectionVisibility?.partners !== false) && (
        <ClientLogosMarquee />
      )}

      {/* 3. Corporate Dark Footer with Page & Section Navigation */}
      <Footer onNavigate={navigateTo} />

      {/* Interactive Detail Modals & Drawers: Lazy-loaded on interaction */}
      <Suspense fallback={null}>
        {selectedDatasheet && (
          <DatasheetModal
            datasheet={selectedDatasheet}
            onClose={() => setSelectedDatasheet(null)}
            onOpenInquiry={handleOpenInquiry}
          />
        )}

        {selectedService && (
          <ServiceModal
            service={selectedService}
            onClose={() => setSelectedService(null)}
            onOpenInquiry={handleOpenInquiry}
          />
        )}

        {selectedIndustry && (
          <IndustryModal
            industry={selectedIndustry}
            onClose={() => setSelectedIndustry(null)}
            onOpenInquiry={handleOpenInquiry}
          />
        )}

        {isConnectDrawerOpen && (
          <ConnectDrawer
            isOpen={isConnectDrawerOpen}
            onClose={() => setIsConnectDrawerOpen(false)}
            onOpenInquiry={handleOpenInquiry}
          />
        )}
      </Suspense>

      {/* Floating Side Quick Dock */}
      <SideQuickDock
        onOpenConnect={() => setIsConnectDrawerOpen(true)}
        onOpenInquiry={handleOpenInquiry}
      />



      {/* Global Engineering RFQ Modal — only mounted when open to allow bfcache */}
      {isEngineeringRfqOpen && (
        <Suspense fallback={null}>
          <EngineeringRfqModal
            isOpen={isEngineeringRfqOpen}
            onClose={() => setIsEngineeringRfqOpen(false)}
            initialScope={rfqInitialScope}
          />
        </Suspense>
      )}

      {/* Global Trust & Compliance Modal — only mounted when open to allow bfcache */}
      {isTrustComplianceModalOpen && (
        <Suspense fallback={null}>
          <TrustComplianceModal
            isOpen={isTrustComplianceModalOpen}
            onClose={() => setIsTrustComplianceModalOpen(false)}
            initialTab={trustModalTab}
            onOpenRfq={(scope) => {
              setIsTrustComplianceModalOpen(false);
              handleOpenRfq(scope);
            }}
          />
        </Suspense>
      )}

      {/* Sleek Progressive Back-to-Top Floating Button */}
      <BackToTop />
    </div>
  );
}

export default function App({ initialPage = 'home' }: { initialPage?: AppPageId } = {}) {
  return (
    <CmsProvider>
      <AppContent initialPage={initialPage} />
    </CmsProvider>
  );
}
